import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '../../lib/api';
import type { ProductListItem } from './types';

interface ArchiveBatchDetail {
  readonly id: string;
  readonly createdAt: string;
  readonly createdByName: string | null;
  readonly note: string | null;
  readonly itemCount: number;
  readonly items: readonly ProductListItem[];
}

/** 归档批次详情（0023）：成员列表 + 逐个恢复 / 整批恢复。 */
export function ArchiveBatchDetailPage(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fmt = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' });

  const { data: batch, isPending } = useQuery({
    queryKey: ['products', 'archive-batches', id],
    queryFn: () => apiFetch<ArchiveBatchDetail>(`/products/archive-batches/${id}`),
    enabled: !!id,
  });

  const refresh = (): void => {
    void queryClient.invalidateQueries({ queryKey: ['products', 'archive-batches', id] });
    void queryClient.invalidateQueries({ queryKey: ['products', 'archive-batches'] });
    void queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
  };

  const restoreOne = useMutation({
    mutationFn: (productId: string) => apiFetch(`/products/${productId}/restore`, { method: 'POST' }),
    onSuccess: () => refresh(),
  });

  const restoreAll = useMutation({
    mutationFn: () => apiFetch(`/products/archive-batches/${id}/restore`, { method: 'POST' }),
    onSuccess: () => refresh(),
  });

  if (isPending || !batch) return <p>{t('app.loading')}</p>;

  return (
    <div>
      <button className="btn btn--ghost btn--sm" onClick={() => navigate('/products/archive-batches')}>
        ← {t('products.archiveBatchesTitle')}
      </button>
      <div className="row row--between">
        <div>
          <h1 style={{ fontSize: 'var(--text-2xl)' }}>{fmt.format(new Date(batch.createdAt))}</h1>
          <p className="field-hint">
            {t('products.archiveBatchByLine', {
              name: batch.createdByName ?? '—',
              note: batch.note ?? '—',
            })}
          </p>
        </div>
        {batch.items.length > 0 && (
          <button
            className="btn btn--primary btn--sm"
            disabled={restoreAll.isPending}
            onClick={() => restoreAll.mutate()}
          >
            {restoreAll.isPending ? t('app.loading') : t('products.archiveBatchRestoreAll')}
          </button>
        )}
      </div>

      {batch.items.length === 0 ? (
        <div className="empty-state">
          <p>{t('products.archiveBatchAllRestored')}</p>
        </div>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th>{t('products.colName')}</th>
                <th>{t('products.colSku')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {batch.items.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.sku}</td>
                  <td>
                    <button
                      className="btn btn--ghost btn--sm"
                      disabled={restoreOne.isPending}
                      onClick={() => restoreOne.mutate(item.id)}
                    >
                      {t('products.archiveBatchRestoreOne')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
